/**
 * Desktop push subscription lifecycle hook (T115).
 *
 * Manages browser push notification permission, subscription via
 * pushManager, and backend registration via push service.
 */

import { useCallback, useEffect, useState } from 'react';
import { pushService, urlBase64ToUint8Array } from '@/services/push';

interface UsePushReturn {
  isSubscribed: boolean;
  isLoading: boolean;
  permission: NotificationPermission;
  error: string | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
}

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Compare a PushSubscription's applicationServerKey against a server VAPID key.
 * Returns true if they match, false on mismatch or if either is null.
 */
function vapidKeysMatch(
  subscriptionKey: ArrayBuffer | null,
  serverKeyBase64: string,
): boolean {
  if (!subscriptionKey) return false;
  const serverKeyBytes = urlBase64ToUint8Array(serverKeyBase64);
  const subKeyBytes = new Uint8Array(subscriptionKey);
  if (serverKeyBytes.length !== subKeyBytes.length) return false;
  for (let i = 0; i < serverKeyBytes.length; i++) {
    if (serverKeyBytes[i] !== subKeyBytes[i]) return false;
  }
  return true;
}

export function usePush(): UsePushReturn {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [error, setError] = useState<string | null>(null);

  // Check for existing subscription on mount; auto-recover on VAPID key mismatch
  useEffect(() => {
    async function checkSubscription() {
      try {
        if (!('serviceWorker' in navigator)) return;
        const registration = await navigator.serviceWorker.ready;
        const sub = await registration.pushManager.getSubscription();

        // Auto-recover: re-subscribe if permission granted but no local subscription
        // (e.g. after VAPID key rotation cleared the old one)
        if (!sub) {
          if (Notification.permission === 'granted') {
            try {
              const { publicKey } = await pushService.getVapidKey();
              const newSub = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
              });
              const p256dh = arrayBufferToBase64(newSub.getKey('p256dh'));
              const auth = arrayBufferToBase64(newSub.getKey('auth'));
              await pushService.subscribe({
                endpoint: newSub.endpoint,
                p256dh,
                auth,
                userAgent: navigator.userAgent,
              });
              setIsSubscribed(true);
            } catch {
              setIsSubscribed(false);
            }
          } else {
            setIsSubscribed(false);
          }
          return;
        }

        // Detect VAPID key change and auto re-subscribe
        try {
          const { publicKey } = await pushService.getVapidKey();
          const currentKey = sub.options.applicationServerKey ?? null;
          if (!vapidKeysMatch(currentKey, publicKey)) {
            await sub.unsubscribe();
            const newSub = await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
            });
            const p256dh = arrayBufferToBase64(newSub.getKey('p256dh'));
            const auth = arrayBufferToBase64(newSub.getKey('auth'));
            await pushService.subscribe({
              endpoint: newSub.endpoint,
              p256dh,
              auth,
              userAgent: navigator.userAgent,
            });
          }
        } catch {
          // VAPID check failed (e.g. offline) — keep existing subscription
        }

        setIsSubscribed(true);
      } catch {
        // Silently handle — not subscribed
      }
    }
    checkSubscription();
  }, []);

  const subscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        setIsLoading(false);
        return;
      }

      const { publicKey } = await pushService.getVapidKey();
      const registration = await navigator.serviceWorker.ready;
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });

      const p256dh = arrayBufferToBase64(pushSubscription.getKey('p256dh'));
      const auth = arrayBufferToBase64(pushSubscription.getKey('auth'));

      await pushService.subscribe({
        endpoint: pushSubscription.endpoint,
        p256dh,
        auth,
        userAgent: navigator.userAgent,
      });

      setIsSubscribed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!('serviceWorker' in navigator)) return;
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();

      if (sub) {
        await sub.unsubscribe();
        await pushService.unsubscribe(sub.endpoint);
      }

      setIsSubscribed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unsubscribe');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isSubscribed, isLoading, permission, error, subscribe, unsubscribe };
}
